import React, { useState, useEffect } from 'react';
import axios from '../config/axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getImageUrl } from '../utils/imageUtils';

const Admin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [artisans, setArtisans] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('tous');
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [visiteAnalytics, setVisiteAnalytics] = useState({
    produitTotal: 0,
    artisanTotal: 0,
    rows: [],
    source: 'fallback'
  });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');
  const [analyticsFilters, setAnalyticsFilters] = useState({
    periode: 'jour',
    type: 'tous',
    recherche: '',
    tri: 'vues-desc'
  });
  const [adminSection, setAdminSection] = useState('stats');
  
  // États pour les modals
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedArtisan, setSelectedArtisan] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [artisanProducts, setArtisanProducts] = useState([]);

  // États pour les formulaires
  const [photoFile, setPhotoFile] = useState(null);
  const [productForm, setProductForm] = useState({
    nom: '',
    description: '',
    prix: '',
    categorie: '',
    quantiteStock: 1,
    reduction: 0,
    estEnPromotion: false,
    materiaux: '',
    dimensions: ''
  });
  const [productPhotos, setProductPhotos] = useState([]);

  const categories = [
    'Textile et Tissage',
    'Poterie et Céramique',
    'Sculpture sur Bois',
    'Maroquinerie',
    'Bijouterie Traditionnelle',
    'Vannerie',
    'Ferronnerie',
    'Peinture et Art',
    'Instruments de Musique',
    'Décoration',
    'Autre'
  ];

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  useEffect(() => {
    loadVisitAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsFilters.periode]);

  const mapAnalyticsResponse = (data) => {
    const toNumber = (value) => Number(value) || 0;

    const list =
      data?.items ||
      data?.rows ||
      data?.entities ||
      data?.resultats ||
      data?.visites ||
      [];

    const rows = Array.isArray(list)
      ? list.map((item, index) => ({
          id: item._id || item.id || `${item.type || 'entite'}-${index}`,
          nom:
            item.nom ||
            item.titre ||
            item.name ||
            item.libelle ||
            item.label ||
            'Sans nom',
          type:
            item.type ||
            item.entityType ||
            item.kind ||
            (item.produitId ? 'produit' : item.artisanId ? 'artisan' : 'produit'),
          vues:
            toNumber(item.vues) ||
            toNumber(item.visites) ||
            toNumber(item.totalVues) ||
            toNumber(item.totalVisites),
          periode: item.periode || analyticsFilters.periode,
          region: item.region || '',
          categorie: item.categorie || '',
          date:
            item.date ||
            item.jour ||
            item.semaine ||
            item.mois ||
            ''
        }))
      : [];

    const produitTotal =
      toNumber(data?.totaux?.produits) ||
      toNumber(data?.totaux?.produit) ||
      toNumber(data?.totalProduitsVues) ||
      rows
        .filter((row) => row.type === 'produit')
        .reduce((sum, row) => sum + row.vues, 0);

    const artisanTotal =
      toNumber(data?.totaux?.artisans) ||
      toNumber(data?.totaux?.artisan) ||
      toNumber(data?.totalArtisansVues) ||
      rows
        .filter((row) => row.type === 'artisan')
        .reduce((sum, row) => sum + row.vues, 0);

    return {
      produitTotal,
      artisanTotal,
      rows,
      source: 'api'
    };
  };

  const buildFallbackAnalytics = (artisansData) => {
    const normalizedArtisans = Array.isArray(artisansData) ? artisansData : [];
    const rows = [];

    normalizedArtisans.forEach((artisan) => {
      rows.push({
        id: `artisan-${artisan._id}`,
        nom: artisan.nom || 'Artisan',
        type: 'artisan',
        vues: Number(artisan.vues) || Number(artisan.visites) || 0,
        periode: analyticsFilters.periode,
        region: artisan.region || '',
        categorie: artisan.specialite || '',
        date: ''
      });

      if (Array.isArray(artisan.produits)) {
        artisan.produits.forEach((produit) => {
          rows.push({
            id: `produit-${produit._id}`,
            nom: produit.nom || 'Produit',
            type: 'produit',
            vues: Number(produit.vues) || Number(produit.visites) || 0,
            periode: analyticsFilters.periode,
            region: artisan.region || '',
            categorie: produit.categorie || '',
            date: ''
          });
        });
      }
    });

    return {
      produitTotal: rows
        .filter((row) => row.type === 'produit')
        .reduce((sum, row) => sum + row.vues, 0),
      artisanTotal: rows
        .filter((row) => row.type === 'artisan')
        .reduce((sum, row) => sum + row.vues, 0),
      rows,
      source: 'fallback'
    };
  };

  const loadVisitAnalytics = async (existingArtisans = artisans) => {
    setAnalyticsLoading(true);
    setAnalyticsError('');
    try {
      const res = await axios.get('/admin/analytics/visites', {
        params: {
          periode: analyticsFilters.periode
        }
      });
      const mapped = mapAnalyticsResponse(res.data);
      setVisiteAnalytics(mapped);
    } catch (error) {
      const fallback = buildFallbackAnalytics(existingArtisans);
      setVisiteAnalytics(fallback);
      if (error.response?.status !== 404) {
        setAnalyticsError('Données partielles: affichage en mode local (fallback).');
      } else {
        setAnalyticsError('Endpoint analytics indisponible: affichage des vues connues localement.');
      }
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [artisansRes, statsRes] = await Promise.all([
        axios.get('/admin/artisans'),
        axios.get('/admin/stats'),
      ]);
      setArtisans(artisansRes.data);
      setStats(statsRes.data);
      await loadVisitAnalytics(artisansRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement données:', error);
      showNotification('Erreur de chargement des données', 'error');
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  const toggleArtisanActive = async (artisanId) => {
    try {
      const res = await axios.put(`/admin/artisans/${artisanId}/toggle-active`);
      showNotification(res.data.message, 'success');
      loadData();
    } catch (error) {
      showNotification(error.response?.data?.message || 'Erreur', 'error');
    }
  };

  const toggleArtisanVerified = async (artisanId) => {
    try {
      const res = await axios.put(`/admin/artisans/${artisanId}/toggle-verified`);
      showNotification(res.data.message, 'success');
      loadData();
    } catch (error) {
      showNotification(error.response?.data?.message || 'Erreur', 'error');
    }
  };

  const updateSubscription = async (artisanId, estAbonne, finAbonnement) => {
    try {
      const res = await axios.put(`/admin/artisans/${artisanId}/subscription`, {
        estAbonne,
        finAbonnement,
      });
      showNotification(res.data.message, 'success');
      loadData();
    } catch (error) {
      showNotification(error.response?.data?.message || 'Erreur', 'error');
    }
  };

  const deleteArtisan = async (artisanId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet artisan et tous ses produits ?')) {
      return;
    }
    try {
      const res = await axios.delete(`/admin/artisans/${artisanId}`);
      showNotification(res.data.message, 'success');
      loadData();
    } catch (error) {
      showNotification(error.response?.data?.message || 'Erreur', 'error');
    }
  };

  // Gestion de la photo de profil
  const openPhotoModal = (artisan) => {
    setSelectedArtisan(artisan);
    setPhotoFile(null);
    setShowPhotoModal(true);
  };

  const handlePhotoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setPhotoFile(e.target.files[0]);
    }
  };

  const handlePhotoSubmit = async (e) => {
    e.preventDefault();
    if (!photoFile) {
      showNotification('Veuillez sélectionner une photo', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('photo', photoFile);

    try {
      const res = await axios.put(`/admin/artisans/${selectedArtisan._id}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showNotification(res.data.message, 'success');
      setShowPhotoModal(false);
      loadData();
    } catch (error) {
      showNotification(error.response?.data?.message || 'Erreur lors du téléchargement', 'error');
    }
  };

  // Gestion des produits
  const openProductModal = async (artisan, product = null) => {
    setSelectedArtisan(artisan);
    setSelectedProduct(product);
    
    if (product) {
      setProductForm({
        nom: product.nom,
        description: product.description,
        prix: product.prix,
        categorie: product.categorie,
        quantiteStock: product.quantiteStock,
        reduction: product.reduction || 0,
        estEnPromotion: product.estEnPromotion || false,
        materiaux: product.materiaux?.join(', ') || '',
        dimensions: product.dimensions || ''
      });
    } else {
      setProductForm({
        nom: '',
        description: '',
        prix: '',
        categorie: '',
        quantiteStock: 1,
        reduction: 0,
        estEnPromotion: false,
        materiaux: '',
        dimensions: ''
      });
    }
    
    setProductPhotos([]);
    
    // Charger les produits de l'artisan
    try {
      const res = await axios.get(`/admin/artisans/${artisan._id}`);
      setArtisanProducts(res.data.produits);
    } catch (error) {
      console.error('Erreur chargement produits:', error);
    }
    
    setShowProductModal(true);
  };

  const handleProductChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProductForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleProductPhotosChange = (e) => {
    if (e.target.files) {
      setProductPhotos(Array.from(e.target.files));
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    
    const formData = new FormData();
    Object.keys(productForm).forEach(key => {
      formData.append(key, productForm[key]);
    });
    
    productPhotos.forEach(photo => {
      formData.append('photos', photo);
    });

    try {
      if (selectedProduct) {
        // Modifier produit existant
        const res = await axios.put(`/admin/produits/${selectedProduct._id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showNotification(res.data.message, 'success');
      } else {
        // Créer nouveau produit
        const res = await axios.post(`/admin/artisans/${selectedArtisan._id}/produits`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showNotification(res.data.message, 'success');
      }
      
      setShowProductModal(false);
      loadData();
    } catch (error) {
      showNotification(error.response?.data?.message || 'Erreur lors de l\'opération', 'error');
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      return;
    }
    
    try {
      const res = await axios.delete(`/admin/produits/${productId}`);
      showNotification(res.data.message, 'success');
      
      // Recharger les produits de l'artisan
      const artisanRes = await axios.get(`/admin/artisans/${selectedArtisan._id}`);
      setArtisanProducts(artisanRes.data.produits);
    } catch (error) {
      showNotification(error.response?.data?.message || 'Erreur', 'error');
    }
  };

  const filteredArtisans = artisans.filter((artisan) => {
    const matchesSearch =
      artisan.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      artisan.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      artisan.specialite.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    switch (filter) {
      case 'actifs':
        return artisan.estActif;
      case 'inactifs':
        return !artisan.estActif;
      case 'abonnes':
        return artisan.estAbonne;
      default:
        return true;
    }
  });

  const filteredAnalyticsRows = visiteAnalytics.rows
    .filter((row) => {
      const typeMatches = analyticsFilters.type === 'tous' || row.type === analyticsFilters.type;
      const search = analyticsFilters.recherche.trim().toLowerCase();
      const searchMatches =
        !search ||
        row.nom.toLowerCase().includes(search) ||
        row.region.toLowerCase().includes(search) ||
        row.categorie.toLowerCase().includes(search);
      return typeMatches && searchMatches;
    })
    .sort((a, b) => {
      if (analyticsFilters.tri === 'vues-asc') return a.vues - b.vues;
      if (analyticsFilters.tri === 'nom-asc') return a.nom.localeCompare(b.nom);
      if (analyticsFilters.tri === 'nom-desc') return b.nom.localeCompare(a.nom);
      return b.vues - a.vues;
    });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-5xl text-primary-600 mb-4"></i>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {/* Notification */}
      {notification.show && (
        <div
          className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg ${
            notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white animate-fade-in`}
        >
          {notification.message}
        </div>
      )}

      {/* Modal Photo Profil */}
      {showPhotoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                Changer la photo de profil
              </h3>
              <button
                onClick={() => setShowPhotoModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Artisan: {selectedArtisan?.nom}</p>
              {selectedArtisan?.photoProfil && (
                <img
                  src={getImageUrl(selectedArtisan.photoProfil)}
                  alt="Profil de l'artisan"
                  className="w-32 h-32 rounded-full object-cover mx-auto mb-4"
                />
              )}
            </div>

            <form onSubmit={handlePhotoSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouvelle photo
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="w-full border border-gray-300 rounded-lg p-2"
                  required
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowPhotoModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Produit */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-4xl w-full p-6 my-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                {selectedProduct ? 'Modifier le produit' : 'Ajouter un produit'}
              </h3>
              <button
                onClick={() => setShowProductModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">Artisan: {selectedArtisan?.nom}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Formulaire */}
              <div>
                <form onSubmit={handleProductSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom du produit *
                    </label>
                    <input
                      type="text"
                      name="nom"
                      value={productForm.nom}
                      onChange={handleProductChange}
                      className="w-full border border-gray-300 rounded-lg p-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description *
                    </label>
                    <textarea
                      name="description"
                      value={productForm.description}
                      onChange={handleProductChange}
                      rows="3"
                      className="w-full border border-gray-300 rounded-lg p-2"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prix (FCFA) *
                      </label>
                      <input
                        type="number"
                        name="prix"
                        value={productForm.prix}
                        onChange={handleProductChange}
                        className="w-full border border-gray-300 rounded-lg p-2"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Stock
                      </label>
                      <input
                        type="number"
                        name="quantiteStock"
                        value={productForm.quantiteStock}
                        onChange={handleProductChange}
                        className="w-full border border-gray-300 rounded-lg p-2"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Catégorie *
                    </label>
                    <select
                      name="categorie"
                      value={productForm.categorie}
                      onChange={handleProductChange}
                      className="w-full border border-gray-300 rounded-lg p-2"
                      required
                    >
                      <option value="">Sélectionner une catégorie</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Réduction (%)
                      </label>
                      <input
                        type="number"
                        name="reduction"
                        value={productForm.reduction}
                        onChange={handleProductChange}
                        min="0"
                        max="100"
                        className="w-full border border-gray-300 rounded-lg p-2"
                      />
                    </div>
                    <div className="flex items-center pt-6">
                      <input
                        type="checkbox"
                        name="estEnPromotion"
                        checked={productForm.estEnPromotion}
                        onChange={handleProductChange}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <label className="ml-2 text-sm text-gray-700">
                        En promotion
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Matériaux (séparés par des virgules)
                    </label>
                    <input
                      type="text"
                      name="materiaux"
                      value={productForm.materiaux}
                      onChange={handleProductChange}
                      placeholder="Coton, Bois, Cuir..."
                      className="w-full border border-gray-300 rounded-lg p-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dimensions
                    </label>
                    <input
                      type="text"
                      name="dimensions"
                      value={productForm.dimensions}
                      onChange={handleProductChange}
                      placeholder="Ex: 30cm x 20cm"
                      className="w-full border border-gray-300 rounded-lg p-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Photos (max 5)
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleProductPhotosChange}
                      className="w-full border border-gray-300 rounded-lg p-2"
                    />
                    {productPhotos.length > 0 && (
                      <p className="text-sm text-gray-500 mt-1">
                        {productPhotos.length} photo(s) sélectionnée(s)
                      </p>
                    )}
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowProductModal(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      {selectedProduct ? 'Mettre à jour' : 'Créer'}
                    </button>
                  </div>
                </form>
              </div>

              {/* Liste des produits existants */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">
                  Produits de cet artisan ({artisanProducts.length})
                </h4>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {artisanProducts.map(product => (
                    <div key={product._id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                      <div className="flex items-start gap-3">
                        {product.photos && product.photos[0] && (
                          <img
                            src={getImageUrl(product.photos[0])}
                            alt={product.nom}
                            className="w-16 h-16 rounded object-cover"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium text-gray-900 text-sm truncate">
                            {product.nom}
                          </h5>
                          <p className="text-sm text-gray-600">{product.prix} FCFA</p>
                          <p className="text-xs text-gray-500">{product.categorie}</p>
                        </div>
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => {
                              setSelectedProduct(product);
                              openProductModal(selectedArtisan, product);
                            }}
                            className="p-1 text-blue-600 hover:text-blue-800"
                            title="Modifier"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            onClick={() => deleteProduct(product._id)}
                            className="p-1 text-red-600 hover:text-red-800"
                            title="Supprimer"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {artisanProducts.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">
                      Aucun produit pour cet artisan
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-lg p-8 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-display font-bold mb-2">
                <i className="fas fa-shield-alt mr-3"></i>
                Panneau d'Administration
              </h1>
              <p className="text-primary-100">Gérez les artisans et les produits de la plateforme</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-primary-100">Connecté en tant que</p>
              <p className="font-semibold">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Artisans</p>
                <p className="text-3xl font-bold text-primary-900">{stats.totalArtisans || 0}</p>
              </div>
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-users text-primary-600 text-xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Artisans Actifs</p>
                <p className="text-3xl font-bold text-green-600">{stats.artisansActifs || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-check-circle text-green-600 text-xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Abonnés</p>
                <p className="text-3xl font-bold text-accent-600">{stats.artisansAbonnes || 0}</p>
              </div>
              <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-star text-accent-600 text-xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Produits</p>
                <p className="text-3xl font-bold text-secondary-600">{stats.totalProduits || 0}</p>
              </div>
              <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-box text-secondary-600 text-xl"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-8 border-b border-gray-200">
          <div className="flex items-center gap-6">
            <button
              onClick={() => setAdminSection('stats')}
              className={`pb-3 text-sm sm:text-base font-semibold transition-colors border-b-2 ${
                adminSection === 'stats'
                  ? 'text-primary-700 border-primary-600'
                  : 'text-gray-500 border-transparent hover:text-primary-600'
              }`}
            >
              Statistiques
            </button>
            <button
              onClick={() => setAdminSection('gestion')}
              className={`pb-3 text-sm sm:text-base font-semibold transition-colors border-b-2 ${
                adminSection === 'gestion'
                  ? 'text-primary-700 border-primary-600'
                  : 'text-gray-500 border-transparent hover:text-primary-600'
              }`}
            >
              Gestion utilisateurs
            </button>
          </div>
        </div>

        {adminSection === 'stats' && (
        <>
        {/* Analytics visites */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                <i className="fas fa-chart-line mr-2 text-primary-600"></i>
                Statistiques de visites
              </h2>
              <p className="text-sm text-gray-500">
                Suivi des vues produits et artisans par jour, semaine ou mois
              </p>
            </div>
            <button
              onClick={() => loadVisitAnalytics()}
              className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700"
            >
              <i className={`fas ${analyticsLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'} mr-2`}></i>
              Actualiser
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Période</label>
              <select
                value={analyticsFilters.periode}
                onChange={(e) => setAnalyticsFilters((prev) => ({ ...prev, periode: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="jour">Jour</option>
                <option value="semaine">Semaine</option>
                <option value="mois">Mois</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Type</label>
              <select
                value={analyticsFilters.type}
                onChange={(e) => setAnalyticsFilters((prev) => ({ ...prev, type: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="tous">Tous</option>
                <option value="produit">Produits</option>
                <option value="artisan">Artisans</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Tri</label>
              <select
                value={analyticsFilters.tri}
                onChange={(e) => setAnalyticsFilters((prev) => ({ ...prev, tri: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="vues-desc">Vues: décroissant</option>
                <option value="vues-asc">Vues: croissant</option>
                <option value="nom-asc">Nom: A-Z</option>
                <option value="nom-desc">Nom: Z-A</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Recherche</label>
              <input
                type="text"
                value={analyticsFilters.recherche}
                onChange={(e) => setAnalyticsFilters((prev) => ({ ...prev, recherche: e.target.value }))}
                placeholder="Nom, région, catégorie..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
              <p className="text-sm text-blue-700">Vues produits ({analyticsFilters.periode})</p>
              <p className="text-2xl font-bold text-blue-900">{visiteAnalytics.produitTotal}</p>
            </div>
            <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4">
              <p className="text-sm text-emerald-700">Vues artisans ({analyticsFilters.periode})</p>
              <p className="text-2xl font-bold text-emerald-900">{visiteAnalytics.artisanTotal}</p>
            </div>
          </div>

          {analyticsError && (
            <div className="mb-4 text-sm rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
              {analyticsError}
            </div>
          )}

          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full min-w-[720px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left text-xs font-semibold uppercase text-gray-500 px-4 py-3">Nom</th>
                  <th className="text-left text-xs font-semibold uppercase text-gray-500 px-4 py-3">Type</th>
                  <th className="text-left text-xs font-semibold uppercase text-gray-500 px-4 py-3">Vues</th>
                  <th className="text-left text-xs font-semibold uppercase text-gray-500 px-4 py-3">Région</th>
                  <th className="text-left text-xs font-semibold uppercase text-gray-500 px-4 py-3">Catégorie</th>
                  <th className="text-left text-xs font-semibold uppercase text-gray-500 px-4 py-3">Période</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredAnalyticsRows.slice(0, 150).map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{row.nom}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${row.type === 'produit' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {row.type === 'produit' ? 'Produit' : 'Artisan'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">{row.vues}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.region || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.categorie || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 capitalize">{row.periode}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!analyticsLoading && filteredAnalyticsRows.length === 0 && (
              <div className="py-8 text-center text-sm text-gray-500">Aucune donnée de visite pour ces filtres.</div>
            )}
            {analyticsLoading && (
              <div className="py-8 text-center text-sm text-gray-500">
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Chargement des statistiques de visites...
              </div>
            )}
          </div>
        </div>
        </>
        )}

        {adminSection === 'gestion' && (
        <>
        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Rechercher un artisan..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <i className="fas fa-search absolute left-3 top-3 text-gray-400"></i>
              </div>
            </div>
            <div className="flex gap-2">
              {['tous', 'actifs', 'inactifs', 'abonnes'].map((key) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    filter === key
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Artisan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Région
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vérifié
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Abonnement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredArtisans.map((artisan) => (
                  <tr key={artisan._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="relative group">
                          <img
                            className="h-10 w-10 rounded-full object-cover cursor-pointer"
                            src={
                              artisan.photoProfil
                                ? getImageUrl(artisan.photoProfil)
                                : '/images/placeholder-artisan.svg'
                            }
                            alt={artisan.nom}
                            onClick={() => openPhotoModal(artisan)}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                               onClick={() => openPhotoModal(artisan)}>
                            <i className="fas fa-camera text-white text-sm"></i>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{artisan.nom}</div>
                          <div className="text-sm text-gray-500">{artisan.specialite}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>{artisan.email}</div>
                      <div className="text-gray-500">{artisan.telephone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {artisan.region}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          artisan.estActif
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {artisan.estActif ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleArtisanVerified(artisan._id)}
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full cursor-pointer ${
                          artisan.estVerifie
                            ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {artisan.estVerifie ? '✓ Vérifié' : 'Non vérifié'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={artisan.estAbonne}
                          onChange={(e) =>
                            updateSubscription(
                              artisan._id,
                              e.target.checked,
                              artisan.finAbonnement
                            )
                          }
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <input
                          type="date"
                          value={
                            artisan.finAbonnement
                              ? new Date(artisan.finAbonnement)
                                  .toISOString()
                                  .split('T')[0]
                              : ''
                          }
                          onChange={(e) =>
                            updateSubscription(
                              artisan._id,
                              artisan.estAbonne,
                              e.target.value
                            )
                          }
                          className="text-xs border border-gray-300 rounded px-2 py-1"
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => openProductModal(artisan)}
                          className="px-3 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 whitespace-nowrap"
                          title="Ajouter un produit"
                        >
                          <i className="fas fa-plus mr-1"></i>Produit
                        </button>
                        <button
                          onClick={() => toggleArtisanActive(artisan._id)}
                          className={`px-3 py-1 rounded ${
                            artisan.estActif
                              ? 'bg-red-100 text-red-700 hover:bg-red-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {artisan.estActif ? 'Désactiver' : 'Activer'}
                        </button>
                        <button
                          onClick={() => deleteArtisan(artisan._id)}
                          className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                        >
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredArtisans.length === 0 && (
            <div className="text-center py-12">
              <i className="fas fa-inbox text-5xl text-gray-300 mb-4"></i>
              <p className="text-gray-500">Aucun artisan trouvé</p>
            </div>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default Admin;
